/**
 * Utility functions for RCS Level-of-Detail (LOD) calculations
 * These utilities are shared across RcsParser, RcsExtractorCore, and RcsFileLoader
 * to avoid code duplication and ensure consistent behavior.
 */

import { LOD_MAPPING_MODE } from './RcsConstants.js';

/**
 * Result of {@link calculateLevelsToProcess}.
 * @typedef {Object} LevelsToProcessResult
 * @property {number[]} levelsToProcess - Array of level indices to load
 * @property {number}   pointsBeforeStart - Number of points before the first loaded level
 * @property {number}   totalPoints - Total number of points that will be loaded
 */

/**
 * Calculate which source level to start loading from.
 *
 * Behavior depends on LOD_MAPPING_MODE:
 *
 * 'threshold' mode (point-based):
 *   - When startPoint = 0: Include level 0 (initial load)
 *   - When startPoint > 0: Skip the level containing startPoint (it was already loaded)
 *     and start from the next level to avoid loading points multiple times
 *
 * 'direct' mode (level-based):
 *   - startPointOrLevel is a source level to load
 *   - Returns that level and the number of points before it
 *
 * @param {Array<number>} numSvoLeafNodes - Array of leaf node counts per level
 * @param {number} startPointOrLevel - Start of point range (threshold mode) or source level (direct mode)
 * @returns {{
 *   startLevel: number,       // Level to start loading from (-1 if beyond all points)
 *   pointsBeforeStart: number // Number of points before the start level
 * }}
 */
export function calculateStartLevel(numSvoLeafNodes, startPointOrLevel) {
  if (LOD_MAPPING_MODE === 'direct') {
    // Direct mode: startPointOrLevel is a source level
    const sourceLevel = startPointOrLevel;

    // Calculate points before this level
    let pointsBeforeStart = 0;
    for (let level = 0; level < sourceLevel && level < numSvoLeafNodes.length; level++) {
      pointsBeforeStart += numSvoLeafNodes[level];
    }

    return {
      startLevel: sourceLevel,
      pointsBeforeStart
    };
  } else {
    // Threshold mode: startPointOrLevel is a point count
    const startPoint = startPointOrLevel;
    let pointsBeforeStart = 0;
    let startLevel = -1;

    for (let level = 0; level < numSvoLeafNodes.length; level++) {
      const leafNodesInLevel = numSvoLeafNodes[level];
      if (pointsBeforeStart + leafNodesInLevel > startPoint) {
        if (startPoint === 0) {
          // Special case: when starting from 0, include level 0
          startLevel = level;
        } else {
          // When starting from a non-zero point, skip the level containing that point
          // (it was already loaded) and start from the next level
          startLevel = level + 1;
          // IMPORTANT: Update pointsBeforeStart to include the skipped level
          // so the caller knows the correct starting point for the next level
          pointsBeforeStart += leafNodesInLevel;
        }
        break;
      }
      pointsBeforeStart += leafNodesInLevel;
    }

    return { startLevel, pointsBeforeStart };
  }
}

/**
 * Calculate levels to process for a given range
 *
 * Behavior depends on LOD_MAPPING_MODE:
 *
 * 'threshold' mode:
 *   startPoint/endPoint are point counts, load levels until endPoint is reached
 *
 * 'direct' mode:
 *   startLevel/endLevel are source levels, load exactly those levels
 *
 * @param {Array<number>} numSvoLeafNodes - Array of leaf node counts per level
 * @param {number} startPointOrLevel - Start of point range (threshold) or source level (direct)
 * @param {number} endPointOrLevel - End of point range (threshold) or source level (direct)
 * @returns {LevelsToProcessResult}
 */
export function calculateLevelsToProcess(numSvoLeafNodes, startPointOrLevel, endPointOrLevel) {
  if (LOD_MAPPING_MODE === 'direct') {
    // Direct mode: load exactly the specified source levels
    const startLevel = startPointOrLevel;
    const endLevel = endPointOrLevel;

    // Calculate points before start level
    let pointsBeforeStart = 0;
    for (let level = 0; level < startLevel && level < numSvoLeafNodes.length; level++) {
      pointsBeforeStart += numSvoLeafNodes[level];
    }

    // No levels to process if out of range
    if (startLevel >= numSvoLeafNodes.length || endLevel < 0) {
      return {
        levelsToProcess: [],
        pointsBeforeStart,
        totalPoints: 0
      };
    }

    // Process levels from startLevel to endLevel (inclusive)
    const levelsToProcess = [];
    let totalPoints = 0;

    for (let level = startLevel; level <= endLevel && level < numSvoLeafNodes.length; level++) {
      levelsToProcess.push(level);
      totalPoints += numSvoLeafNodes[level];
    }

    return {
      levelsToProcess,
      pointsBeforeStart,
      totalPoints
    };
  } else {
    // Threshold mode: original point-based logic
    const startPoint = startPointOrLevel;
    const endPoint = endPointOrLevel;

    const { startLevel, pointsBeforeStart } = calculateStartLevel(numSvoLeafNodes, startPoint);

    // If startLevel is -1 or beyond the last level, no points to load
    if (startLevel === -1 || startLevel >= numSvoLeafNodes.length) {
      return {
        levelsToProcess: [],
        pointsBeforeStart,
        totalPoints: 0
      };
    }

    // If the previous LOD already exceeded this LOD's endPoint, don't load anything
    // This happens when LOD 0 loads more points than LOD 1's threshold
    // Example: LOD 0 target is 1000 but loads 21777 (full level), LOD 1 range is 1000-10000
    // Since 21777 > 10000, LOD 1 should load 0 points
    if (pointsBeforeStart >= endPoint) {
      return {
        levelsToProcess: [],
        pointsBeforeStart,
        totalPoints: 0
      };
    }

    // Process levels from startLevel until we reach or exceed endPoint
    const levelsToProcess = [];
    let cumulativeLeafNodes = 0;

    for (let level = startLevel; level < numSvoLeafNodes.length; level++) {
      const leafNodesInLevel = numSvoLeafNodes[level];
      levelsToProcess.push(level);
      cumulativeLeafNodes += leafNodesInLevel;

      // Continue until we have at least endPoint total points from the start of the file
      if (pointsBeforeStart + cumulativeLeafNodes > endPoint) {
        break;
      }
    }

    return {
      levelsToProcess,
      pointsBeforeStart,
      totalPoints: cumulativeLeafNodes
    };
  }
}